import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { finalize, Subscription, takeUntil } from 'rxjs'
import { show } from '@schmancy/overlay/overlay.service'
import { inExemptLayer } from '@schmancy/overlay/overlay.stack'
import { firstFocusableDescendant } from '@schmancy/utils/focus'
import '../button/icon-button'

// Arrow/Home/End moves .focus() imperatively across menu items; Tab cycling
// is owned separately by the overlay's own focus trap (wireTabTrap()).
const ROVING_ITEM_SELECTOR = 'schmancy-menu-item, schmancy-list-item'

function focusableItems(container: HTMLElement): HTMLElement[] {
	return Array.from(container.querySelectorAll<HTMLElement>(ROVING_ITEM_SELECTOR))
}

function moveRovingFocus(container: HTMLElement, e: KeyboardEvent): void {
	const items = focusableItems(container)
	if (items.length === 0) return
	const active = (e.composedPath().find(n => n instanceof HTMLElement && items.includes(n)) ??
		null) as HTMLElement | null
	const currentIndex = active ? items.indexOf(active) : -1

	let nextIndex: number | null = null
	switch (e.key) {
		case 'ArrowDown':
			nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length
			break
		case 'ArrowUp':
			nextIndex = currentIndex < 0 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length
			break
		case 'Home':
			nextIndex = 0
			break
		case 'End':
			nextIndex = items.length - 1
			break
		default:
			return
	}
	e.preventDefault()
	// The item wrapper has no tabindex of its own; the real focusable node is nested in its shadow root.
	const target = items[nextIndex]
	if (target) (firstFocusableDescendant(target) ?? target).focus()
}

@customElement('schmancy-menu')
export default class SchmancyMenu extends SchmancyElement {
	static styles = [
		css`
			:host {
				position: relative;
				display: flex;
			}
		`,
	]

	@query('slot:not([name])')
	private menuSlot!: HTMLSlotElement

	@query('slot[name="trigger"]')
	private triggerSlot!: HTMLSlotElement

	@query('slot[name="button"]')
	private buttonSlot!: HTMLSlotElement

	/** Reflected so a consumer's trigger element can bind `aria-expanded` to the menu's open state. */
	@property({ type: Boolean, reflect: true, attribute: 'open' })
	open = false

	/** Element to refocus on dismissal, since the browser otherwise strands focus on <body>. */
	private triggerEl: HTMLElement | null = null

	/** The open menu's lifecycle — unsubscribing it is what `close()` dismisses through. */
	private overlay$: Subscription | null = null

	private showMenu = (): void => {
		// Flattened, as a component composing this menu forwards its own slot into it. Each item goes home to its own parent.
		const menuItems = this.menuSlot?.assignedElements({ flatten: true }) || []
		// Each leaves a marker where it stood, inside whatever range of a template owns it, so that template can still
		// move or remove it there: an item whose marker went with its template has no home to come back to.
		const homes = menuItems.map(item => {
			const home = document.createComment('')
			item.before(home)
			return home
		})
		if (menuItems.length === 0) return

		this.triggerEl =
			(this.triggerSlot?.assignedElements()[0] as HTMLElement | undefined) ??
			(this.buttonSlot?.assignedElements()[0] as HTMLElement | undefined) ??
			null

		const overlayContainer = document.createElement('div')
		// The items carry the menuitem role; the container that adopts them is
		// the only element in the tree that can carry the menu they belong to.
		overlayContainer.setAttribute('role', 'menu')
		// The panel mounts beside the page, away from this host: it joins the
		// exempt layer only by carrying the mark its host sits under.
		overlayContainer.toggleAttribute('data-overlay-exempt', inExemptLayer(this))
		overlayContainer.style.padding = '0.5rem'
		menuItems.forEach(item => overlayContainer.appendChild(item))
		overlayContainer.addEventListener('keydown', e => moveRovingFocus(overlayContainer, e))

		this.open = true

		this.overlay$ = show(overlayContainer)
			.pipe(
				// finalize, not the observer's `complete`: an imperative close()
				// unsubscribes rather than completing, and the items still have to
				// come home and the trigger still has to take focus back.
				finalize(() => {
					menuItems.forEach((item, index) => {
						const home = homes[index]
						if (home?.parentNode) home.replaceWith(item)
						else item.remove()
					})
					this.open = false
					this.dispatchEvent(new CustomEvent('menu-close'))
					this.overlay$ = null
					this.triggerEl?.focus()
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	/**
	 * Dismiss the open menu. A consumer holding only the `<schmancy-menu>` tag
	 * — the whole public surface — otherwise has no way to close it but to
	 * reach through the overlay's shadow root for the element that listens.
	 * A no-op when nothing is open.
	 */
	close(): void {
		this.overlay$?.unsubscribe()
	}

	render() {
		return html`
			<slot name="trigger" @click=${this.showMenu}>
				<slot name="button" @click=${this.showMenu}>
					<schmancy-icon-button>more_vert</schmancy-icon-button>
				</slot>
			</slot>
			<div hidden>
				<slot></slot>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-menu': SchmancyMenu
	}
}
